import { type ProductClient } from "@fitness-app/data-models/entities/ProductClient";
import { getErrorMessage } from "@fitness-app/utils";

import { type AppThunk } from "../../../index";
import {
  subscribeToProductClientFailed,
  subscribeToProductClientStarted,
  subscribeToProductClientSuccess,
  updateProductClient,
} from "../reducer";
import { fetchProductClient } from "./fetchProductClient";
import { unsubscribeFromProductClient } from "./unsubscribeFromProductClient";
import { openRealtimeChannel } from "../../../helpers/openRealtimeChannel";
import { claimRealtimeSlot, ownsRealtimeSlot } from "../../../helpers/realtimeSlot";

export const subscribeToProductClient =
  (productId: string, clientEmail: string | null, id?: string): AppThunk =>
  async (dispatch, _getState, { subscriptions, db, errorTrackingService }) => {
    const claimedSlot = claimRealtimeSlot(subscriptions, "productClient");

    try {
      dispatch(subscribeToProductClientStarted());

      const client = await dispatch(fetchProductClient({ productId, clientEmail: clientEmail || "", id })).unwrap();

      // a newer subscribe or an unsubscribe took the slot while we were fetching
      if (!ownsRealtimeSlot(subscriptions, "productClient", claimedSlot)) {
        return;
      }

      const channelName = `product-client-${productId}-${clientEmail}`;
      const channel = await openRealtimeChannel(db, channelName, subscriptions.realtimeProductClientChannel);

      // detaching the previous channel is another await - the slot may have changed hands
      if (!ownsRealtimeSlot(subscriptions, "productClient", claimedSlot)) {
        await db.removeChannel(channel);
        return;
      }

      subscriptions.productClientChannelName = channelName;
      subscriptions.realtimeProductClientChannel = channel;

      subscriptions.realtimeProductClientChannel
        .on<ProductClient>(
          "postgres_changes",
          {
            event: "UPDATE",
            schema: "public",
            table: "product_client",
            filter: `id=eq.${client.id}`,
          },
          (payload) => {
            if (payload.eventType === "UPDATE") {
              dispatch(updateProductClient(payload.new));
            }
          },
        )
        .subscribe((status, error) => {
          if (error) {
            errorTrackingService?.recordError(error, "product_client_subscribe_error", {
              product_client_id: productId,
              client_email: clientEmail,
              error: error.message,
            });
          }
          if (status === "TIMED_OUT") {
            dispatch(unsubscribeFromProductClient());
          } else {
            dispatch(subscribeToProductClientSuccess());
          }
        });
    } catch (error) {
      dispatch(subscribeToProductClientFailed(getErrorMessage(error)));
    }
  };
